import User from "@Components/app/members/teamInterfaces";
import { IMember } from "@Containers/game/settings/profile";
import { UserState } from "@Context/common/general/initial-state.types";
import { UserRankResult } from "@Globals/types";
import { getAvatarColorScheme, getInitials } from "@Helpers/avatar-color-utils";
import { Avatar, AvatarProps } from "@mui/material";
import { UserRecord } from "@Shared/schemas/users/userSchemas";
import { useMemo } from "react";

type DBUser = UserRecord | IMember;
type GlobalUser = UserState;
type LegacyUser = User | UserRankResult; // Firestore fields
export type AvatarUser = DBUser | GlobalUser | LegacyUser;

function normalizeUser(user?: AvatarUser): { identifier: string; firstName: string; lastName: string } {
  if (!user) return { identifier: "", firstName: "", lastName: "" };

  if ("first_name" in user || "last_name" in user) {
    const userRecord = user as DBUser;
    return {
      identifier: userRecord.doc_id || "",
      firstName: userRecord.first_name || "",
      lastName: userRecord.last_name || "",
    };
  }

  if ("data" in user) {
    const globalUserState = user as GlobalUser;
    return {
      identifier: globalUserState.data?.doc_id || "",
      firstName: globalUserState.first || "",
      lastName: globalUserState.last || "",
    };
  }

  const legacyUser = user as LegacyUser;
  return {
    identifier: legacyUser.doc_id || "",
    firstName: legacyUser.first || "",
    lastName: legacyUser.last || "",
  };
}

export interface IBaseAvatar extends AvatarProps {
  user?: AvatarUser;
}

const BaseAvatar = ({ user, src, children, sx, ...props }: IBaseAvatar) => {
  const { identifier, firstName, lastName } = normalizeUser(user);

  const colorScheme = useMemo(() => getAvatarColorScheme(identifier), [identifier]);
  const initials = useMemo(() => getInitials(firstName, lastName), [firstName, lastName]);

  return (
    <Avatar
      alt="profile-avatar"
      src={src}
      {...props}
      sx={{
        color: "common.black",
        backgroundColor: user ? colorScheme.backgroundColor : "dsSurface.v300",
        fontWeight: 500,
        fontSize: `0.875rem`,
        ...sx,
      }}
    >
      {children || (!src && initials)}
    </Avatar>
  );
};

export default BaseAvatar;